import appState, {
  WORKSPACE_SCOPED_KEYS as APP_WORKSPACE_SCOPED_KEYS,
  WORKSPACE_SCOPED_OVERRIDES
} from './state-app.js'

// Shell state schema — all four scope tiers in a single flat object.
//
// Scope cascade (larger scope clears smaller when switching):
//   USER → ORG → ┬── WORKSPACE → PROJECT
//                └── WORKSPACE PROJECT
//
// Switcher functions in shared/functions/switchers/ manage clearing.
// Activity entities (tickets, chats) are NOT stored here — they are
// fetched declaratively via fetch: props and cached by the fetch plugin.
// Named so a consumer can import the resolved defaults by NAME. A DEFAULT
// import is not a statically-resolvable binding for frank's globalScope
// lifter, so `scopes.js`'s wipe read of it emitted an unresolvable
// identifier in the published payload (FRANK gate, WORKSPACE-SHELL-FRANK-
// GATE-BLOCKS-REPUBLISH-1). The default export stays for the `state`
// section contract.
export const STATE_DEFAULTS = {
  ...appState,

  // USER scope — logout invalidates
  user: null,
  preferences: {},
  themeMode: 'auto',
  // The campaigns card's group-by pill (WS-ANALYTICS-UTM-CAMPAIGNS-UI-1) —
  // the same class as `themeMode`: a USER view preference ('campaign' |
  // 'source' | 'medium' | 'content' | 'term'), owned and also declared by
  // @symbo-ls/workspace-analytics/state.js, declared HERE so the partition +
  // census classify it unscoped (WORKSPACE_UNSCOPED_KEYS below). Every other
  // analytics key is tenant data with a reset shape in state-app.js instead.
  // (The Local | UTC `_analyticsTz` preference is retired —
  // WS-ANALYTICS-REMOVE-UTC-DISPLAY-TOGGLE-1.)
  _analyticsCampaignsGroupBy: 'campaign',
  // ── Accessibility preferences (2026-09-04) ──
  // Per PERSON and per DEVICE, exactly like `themeMode` beside them: written
  // only by shared/functions/a11yPrefs.js, persisted to one localStorage key
  // and mirrored onto <html> for the shell sheet to scope on. Declared here so
  // the root-write census can classify them (WORKSPACE_UNSCOPED_KEYS below —
  // a workspace switch must never take someone's larger type away).
  a11yFontScale: 'md',
  a11yContrast: false,
  a11yMotion: false,
  a11yLinks: false,
  // The RESOLVED theme (`'dark'|'light'`) — `themeMode` above is the MODE,
  // never what `'auto'` resolves to. bootShell.js (shared) overwrites this
  // default with the real boot-time value and keeps it live for the whole
  // session via a `data-theme` MutationObserver (themeResolved.js) — see
  // tickets/opus.md "DECIDED: themeResolved". Declared here (mirrors the
  // themeMode declaration one line up) purely to document the key on this
  // app's canonical schema.
  themeResolved: null,
  // White-label branding for the CURRENT host, resolved pre-auth from the
  // org's record (WHITELABEL-BRANDED-BOOT-1, functions/whitelabelBrand.js).
  // `null` on every platform host and for any custom domain with no record —
  // which is what makes every consumer's fallback the generic Symbols
  // chrome. Display fields only; the server never projects persona user ids.
  // Declared here so smbls 3.14's typed-bag `state.update()` keeps the key
  // on the late-apply write.
  whitelabelBrand: null,
  // The host-scope pin MIRROR (customDomainBoot.js writes it the moment the
  // scope lookup lands; WhitelabelRequestAccess subscribes to it). Declared
  // so the key exists from FIRST render — an absent key registers no
  // dependency — and so the typed bag keeps it. `null` = no opinion, same
  // contract as hostScopePin.js.
  _hostScopeOrgId: null,
  // The TENANT this white-label host is bound to (WHITELABEL-STRICT-TENANT-
  // SCOPE-1, functions/whitelabelBrand.js seedWhitelabelHostScope), resolved
  // pre-auth and independent of branding: `{ organizationId, workspaceId,
  // workspaceKey, domain }` on a bound customer host, `false` on a customer
  // host whose tenant could not be established (render no tenant), `null` on
  // every platform host. Declared so the typed bag keeps the pre-mount seed.
  whitelabelHostScope: null,
  // `whitelabelSigninError` was declared here and REMOVED 2026-08-19: its only
  // writer was `functions/whitelabelSignin.js`, which `cbbf88fa0` deleted
  // ("REVERT the white-label LoginWindow override — it silently destroyed the
  // Google/GitHub column"). The declaration outlived it with zero readers, so
  // this was never a scoping question. The live refusal message is
  // component-local in shared/components/WhitelabelSignin.js, and
  // __tests__/whitelabelBrand.test.js already bans the deleted file from
  // returning. Re-verified before removal: one occurrence in the whole tree —
  // this declaration — across workspace/packages, server, smbls, company, docs
  // and sdk, including a search for computed-key writers.
  lang: 'en',

  // ORG scope — switchOrg invalidates (cascades to all below)
  // `orgs` is the membership list (from JWT app_metadata.orgs); `org` is
  // the active one. AppWorkspaceSwitcher reads from JWT claims directly;
  // shell-state copies are the canonical "you're signed in to N orgs"
  // surface for everywhere else.
  orgs: [],
  org: null,
  permissions: null,
  orgMembers: null,
  orgRoles: null,
  orgTeams: null,

  // WORKSPACE scope — switchWorkspace invalidates
  // `workspaces` is the membership list (from JWT app_metadata.workspaces);
  // `workspace` is the active one matching `active_workspace_id`.
  workspaces: [],
  workspace: null,
  // True ONLY while `root.workspace` holds a COMPLETE record (`_shape:'full'`
  // — has `settings`), false while it holds a list/claims-shaped identity-only
  // entry (or nothing). Written EXCLUSIVELY by
  // shared/functions/activeWorkspaceRecord.js's `setActiveWorkspaceRecord` —
  // see that file for the full shape-discipline contract
  // (tickets/spec-workspace-record-shape.md). NOT the same thing as
  // "workspace.settings is non-empty" — a workspace can legitimately carry no
  // settings overrides and still be `ready`. Consumers rendering
  // configuration derived from `workspace.settings.*` (navbar, designSystem,
  // modules, …) MUST branch on this instead of assuming a present `workspace`
  // means a complete one.
  workspaceReady: false,
  workspaceMembers: null,
  workspaceIntegrations: null,
  workspaceBilling: null,
  // Live credit balance for the guard surfaces (CreditBlocker/CreditBanner
  // + threshold notifications) — sdk.getCreditBalance(wsId) payload, fetched
  // by functions/creditGuard.js on boot + workspace switch. Workspace-scoped:
  // a previous tenant's balance must never gate (or un-gate) the next one.
  _creditGuard: null,
  // State mirror of the banner's per-(workspace, month, level) dismissal
  // pref, so the hide re-evaluates immediately on dismiss (prefs alone are
  // not reactive). Workspace-scoped for the same isolation reason.
  _creditBannerDismissed: null,
  // CREDIT-BLOCK-1 (tickets/opus.md) — session dismissal of the exhausted
  // full-viewport notice (Escape / ✕ / backdrop). Deliberately NOT a pref
  // cookie like the banner's: a `smbls_pref_*` cookie's one-year, cross-app,
  // cross-tab scope would outlive the tab, so a genuinely new session would
  // never see the notice again — running out of credits is a hard fact that
  // should restate once per session, not be silenced for a year.
  // Workspace-scoped like its siblings — dismissing for one tenant must
  // never suppress the notice for the next one.
  //
  // CREDITS-GATE-UI-BREAK-1 (tickets/sonnet.md): this key alone is
  // in-memory root state, wiped by ANY remount — including ordinary
  // dev-server HMR/live-reload churn, not just a real reload — so the gate
  // re-armed on every single page mount. `components/CreditBlocker.js`'s
  // `scope.close` now ALSO mirrors the dismissal into per-workspace
  // sessionStorage (`writeCreditBlockerDismissed`,
  // functions/creditGuard.js) and its `Overlay.hide` seeds this key back
  // from there on the next mount (`readCreditBlockerDismissed`) — still
  // session-scoped (sessionStorage dies with the tab, so a genuinely new
  // tab sees the gate once), just no longer reset by an in-tab remount.
  _creditBlockerDismissed: null,
  // The shortfall of the last AI request the server refused for credits —
  // `{ needed, available, at, msgId, dismissed }`, or null. Held by
  // functions/creditGuard.js `notifyCreditsExhaustedFromMessage` (the toast
  // alone is gone in seconds) so CreditBanner can keep the way out on screen —
  // an admin's "Add credits", a member's "Ask an admin" — until the balance
  // covers it or the person closes it, and so /admin/usage can say "Not enough
  // for your last AI request" instead of "Healthy balance". `dismissed` hides
  // the banner only; the record stays true until the balance covers it.
  // Workspace-scoped like its siblings: a previous tenant's shortfall must
  // never warn (or reassure) the next one.
  _creditsShort: null,
  // DemoClaimBanner — the new email a demo claim parked on the server's
  // `pendingEmail` (verification link not clicked yet), and whether the
  // server could mail that link. Declared so the claim window's `hide` /
  // `text` reads subscribe from the first render.
  _demoClaimPendingEmail: null,
  _demoClaimPendingSent: null,

  // Persona (tickets/sonnet.md PERSONA-5 — the shell-state half of PERSONA-1
  // ..6). `_personaSession` is the DOMQL-state reflection of the sessionStorage
  // blob personaSession.js owns (`personaStateFromSession` shape: { role, sid,
  // startedAt, expiresAt, workspaceId, actorWorkspaceRole } | null) — the JWT
  // itself never enters state (see that file's header). components/
  // PersonaPill.js is the SOLE reader/writer of these three keys (`personaActive`,
  // the RoleToggle label, the ExitBtn) — do not add a second copy of persona UI
  // state anywhere else; extend this one.
  //
  // Declared here (not left to smbls 3.14's create-signal-on-first-write)
  // because EVERY write below was a silent no-op before this line existed —
  // v3.14's typed-bag root.update() drops writes to undeclared keys, so
  // `endPersonaSession`'s `root.update({ _personaSession: null, ... })` and
  // every other write in shared/functions/persona.js + components/
  // PersonaPill.js landed on the floor. PERSONA-1..4 shipped a fully working
  // server + SDK + client-plumbing stack around a state key that could never
  // actually hold a value client-side.
  //
  // Workspace-SCOPED (below, WORKSPACE_SCOPED_KEYS): a persona session is
  // bound to one workspace (`workspaceId` in the blob; the server's sid
  // resolver refuses cross-tenant reuse — 886a9b27) and `sdk.switchWorkspace`/
  // `switchOrg` already end the persona server-side + restore the admin's
  // token via PersonaService's own switchWorkspace/switchOrg hooks (sdk
  // src/services/PersonaService.js) — so a workspace switch means "the persona
  // ended" is already true API-side by the time onWorkspaceChange's PHASE-1
  // wipe runs. Registering the key here just makes the CLIENT admit the same
  // truth: without it, a stale `_personaSession` would keep painting "Viewing
  // as <role>" over the NEW workspace after the persona that role referred to
  // was already gone — the exact "registered but the wrong state survives"
  // half of the standing isolation invariant. `_personaPickerOpen` /
  // `_personaExiting` are pure transient UI state for the SAME pill, wiped for
  // the same reason (an open picker or a stuck "Exiting…" must never carry
  // into the next workspace's first paint).
  _personaSession: null,
  _personaPickerOpen: false,
  _personaExiting: false,

  // Org-switcher transient UI flags. Declared explicitly because smbls
  // 3.14 root.update() drops keys not in the schema (treats state as a
  // typed bag). `_wsSwitching` drives AppWorkspaceSwitchOverlay;
  // `_pendingActiveOrgId` is the optimistic-pick override the org
  // switcher reads so the dropdown reflects the click instantly,
  // without waiting on the server JWT refresh round-trip.
  //
  // (Removed 2026-06-03: _wsMenuOpen — AppOrgSwitcher migrated to
  // @symbo.ls/dropdown which owns visibility state internally.)
  _wsSwitching: null,
  // Boot fix step 4b: true while the installed apps of THIS workspace set up
  // in their idle slices after the reveal (functions/workspaceProjects.js
  // _runAppActivations). The home keeps a saved app cell's skeleton while it
  // is up (pages/main.js _moduleRoutesPending); the queue drops it on drain.
  // Declared so the PendingWidgets subscription sees every flip.
  _appsSettingUp: false,
  _pendingActiveOrgId: null,
  _loginLoading: false,
  // The address a finished sign-up hands to the sign-in form when it could
  // not open the new account itself (shared pages/auth/signup.js
  // handToSignIn; signin.js reads it once and clears it). Never in the URL.
  _signInEmail: '',
  // Re-render signal of the runtime loader's ready hold (app.js THE READY HOLD).
  _loaderReadyHold: 0,
  // A sign-in ON this page has not applied the session's interface language
  // yet: the runtime loader holds and shows no word. Raised and paid by
  // functions/shellLangPolicy.js (markShellLangOwed / applyShellLangPolicy).
  _langOwed: false,
  // WorkspaceProjectSwitcher raises this synchronously on click so the
  // brand MainLoader surfaces before the SDK round-trip + fetch-cache
  // invalidation completes. Cleared in the switcher's try/finally.
  _wpSwitching: null,
  // Per-session "the server rejected setActiveOrganization for this org id"
  // cache. `AppOrgSwitcherItem.onClick`'s 403 catch pushes here so the
  // org disappears from the AppWorkspaceSwitcher menu for the rest of
  // the session — defensive against stale JWT `app_metadata.orgs` that
  // includes orgs the user has no `OrgMember` row for. Reset on the next
  // full sign-in cycle (state schema is fresh per shell mount).
  _invalidOrgs: [],
  // Root-mounted MainLoader's reactive visibility binds to this flag.
  // `applyLoader({ value: true|false })` writes through here so
  // long-running flows (org switch, project create, etc.) can surface
  // the brand loading overlay. Must be declared in the schema — smbls
  // 3.14 root.update() drops keys missing from the typed bag.
  loading: false,

  // Private data — populated by loadPrivateData on sign-in / org-switch.
  peopleAll: [],
  // Populated by useOnlinePresence (subscribed in app.js onCreate).
  onlineUsers: [],
  teamsByEmail: {},
  // Primary team-membership index, keyed on the stable non-PII userId that
  // rides every roster row (server memberProjection.js). teamsByEmail is a
  // legacy alias kept for mgmt viewers / optimistic writes where an email is
  // known — but a non-mgmt viewer's roster payload has NO email, so the id
  // index is the one that resolves for them (tickets/fable.md P0 regression).
  teamsByUserId: {},
  orgMembersByOrgId: {},
  // Per-team "members already fetched this session" flags — `{ [teamId]: true }`.
  // MUST be declared here: smbls 3.14 root.update() silently drops any key
  // missing from this typed schema, so without it `loadTeamMembers`' (teams.js)
  // idempotency guard never persisted — every loadPrivateData fire (sign-in,
  // ~hourly token refresh, org switch) re-fetched every team's members,
  // storming GET /organizations/:org/teams/:team/members.
  teamMembersLoaded: {},
  userDocs: null,
  userPrefs: null,
  // Home dashboard prefs (workspace-scoped): the user's own hidden-widget set +
  // grid layout (userDashboardPrefs, per user × workspace) and the workspace
  // admin default panel set (workspaceSettings). See migration 0157 + main.js.
  userDashboardPrefs: null,
  // True once the home-dashboard prefs GET for the ACTIVE workspace has
  // SUCCEEDED this scope (even when it returned no row). Distinguishes "the
  // user has no saved row" from "the read failed/hasn't landed" — the grid
  // boot must never persist its default hidden set over an UNREAD row (that
  // clobber is how a degraded boot rewrote saved dashboards to
  // greeting-only, 2026-07-28). Wiped on workspace switch below.
  _dashPrefsReadOk: false,
  // Per-workspace settings bag. The server persists `home_default_panels` today;
  // applyWorkspaceConfig (functions/workspaceConfigs.js) also resolves four
  // frontend fields on workspace activation and merges them here:
  //   home_default_panels — admin default home panels (server)
  //   designSystem        — personalized skin { canvasBg, vars } | null
  //   language            — BCP-47 lang for <html lang>
  //   layoutDirection     — 'ltr' | 'rtl' for <html dir>
  //   navbar              — HeaderTabs arrangement [{key,label,path}] (default
  //                         Studio/Tasks/Files; GreenIG → Claims/Users/Calendar)
  //   modules             — enabled workspace module ids | null
  workspaceSettings: null,
  resourceLinks: [],
  intranetEvents: [],
  // The calendar window has answered (loadPrivateData), so an empty
  // `intranetEvents` is a real "nothing", not "not read yet" — or it failed,
  // and the home Next up widget says so instead of a skeleton that waits
  // forever. Both are wiped with the events on a workspace switch.
  intranetEventsLoaded: false,
  intranetEventsError: false,
  jwtClaims: null,
  signedIn: false,
  // Auth-resolution gate. Flipped to true synchronously inside the
  // shell's `onCreate` once the pre-resolved auth snapshot from
  // bootShell has been seeded into root (or once subscribeAuthSession
  // has fired at least once on the anonymous-boot path). Reactive
  // consumers that would otherwise flash with schema-default values
  // (NoOrgOverlay's empty create-team modal, sign-out chrome, etc.)
  // hide until this flips. Without it, the window between mount and
  // the first auth fire painted an incorrect frame for every signed-in
  // user.
  _authReady: false,
  // True ONLY while the currently-painted chrome (org/workspace/roster —
  // the fields `context.preResolvedAuth` carries) came from
  // bootSnapshot.js's warm-start cache rather than a confirmed live
  // response (tickets/opus.md "boot: the scope-keyed warm-start snapshot's
  // PAINT half"). bootShell flips it back to `false` the moment the REAL
  // `/core/boot` round trip resolves and reconciles over the seed — never
  // reuse `workspaceReady` for this (that flag means "confirmed complete
  // record"; overloading it would let an unconfirmed cache paint as
  // authoritative). Declared here so smbls 3.14's typed-bag
  // `state.update()` doesn't silently drop it on that reconcile write.
  _bootFromCache: false,
  currentUserEmail: '',
  currentUserId: '',
  // Legacy external auth UUID (nothing mints new ones). Mongo `_id` and
  // the legacy UUID are different identifiers for the same person;
  // most legacy meet/canvas/governance rows (`meet_rooms.created_by`,
  // `canvas_pages.created_by`, RLS-scoped tables) reference the
  // legacy UUID, while `currentUserId` above carries the Mongo
  // _id. Host-only gates on those rows compare against this field —
  // see ControlBar.SettingsBtn / EndRoomBtn — so a federated user
  // who created a meet room can actually unlock the room-settings
  // pane on their own call.
  currentUserSupabaseId: '',
  // Mongo `User.username` — declared so it survives smbls 3.14's
  // "typed bag" root.update() (any key not in this schema is silently
  // dropped). bootShell seeds it from the /core/auth/me response so
  // components that build `/user/:slug` links (header dropdown, share
  // sheets, etc.) have the canonical slug instead of falling back to
  // the email local-part.
  currentUserUsername: '',
  orgNotifCount: {},

  // PROJECT scope — canvas-internal switchProject invalidates
  project: null,
  projectComponents: null,
  projectPages: null,
  projectState: null,
  createMenuOpen: false,
  _addPagePicker: null,
  _addComponentPicker: null,

  // Public share-link dialog (PUBLIC-SHARE-LINKS-1) — ephemeral UI state for
  // the "Share publicly" modal, opened from /files and /notes alike.
  // WORKSPACE-scoped on purpose: the target is a file or a note that belongs
  // to ONE workspace, so a switch must not leave the dialog holding another
  // tenant's resource (or, worse, its freshly-minted URL) on screen.
  // `_shareNewUrl` carries the one-time link the server will never return
  // again; it lives here for the life of the dialog and nothing persists it.
  _shareTarget: null,
  _shareLinks: [],
  _shareBusy: false,
  _shareLoading: false,
  _shareError: null,
  _shareNewUrl: null,
  _shareExpiryKey: '30',

  // Public share-link VIEWER (/s/<token>, PUBLIC-SHARE-LINKS-1) — the
  // recipient's read state. Deliberately NOT workspace-scoped: the person on
  // this page has no workspace, no org and no session, and the surface is
  // reached by URL only. See WORKSPACE_UNSCOPED_KEYS below.
  _sharedStatus: null,
  _sharedDoc: null,
  _sharedToken: null,
  // The file link's byte URL, resolved once when the payload lands (the
  // SDK proxy is async — a render factory cannot await).
  _sharedContentUrl: null,

  // WORKSPACE PROJECT scope — switchWorkspaceProject invalidates
  workspaceProject: null,

  // Navigation
  currentPath: '/',
  currentSearch: '',
  // The catch-all route's "Not found" grace latch
  // (WS-PREBOOT-NOT-FOUND-FLASH-ON-MODULE-ROUTE-1, pages/projectRedirect.js).
  // The catch-all's onInit arms a timer; when it fires, this flips to true
  // and NotFoundBody may show its copy although `_appRoutesSettled` never
  // latched (a signed-out visit runs no route loaders). One-way for the page
  // load: no code writes it back to false. Declared so the key exists from
  // the FIRST render and the root-write census can classify it.
  _notFoundGraceElapsed: false,

  // Unified-shell flag — sub-apps (canvas, …) read this to suppress their
  // own AppHeader when mounted inside the shell. Standalone wrappers
  // (canvas.localhost) do not set it, so app-specific headers keep rendering.
  _inShell: true,

  // ── WS-ROOT-STATE-FORM2-WRITE-HOLE-1 — Form-2 census drain, batch 2. ──
  // Eleven keys written by direct assignment (`root.key = …`) left the
  // ROOT_WRITE_UNDECLARED baseline for the declared universe. Scope verdicts
  // live in WORKSPACE_UNSCOPED_KEYS below (one block, argued per key).
  //
  // Canvas agents-page LENSES — seeded by the page's own onCreate
  // (`if (root._agentsScope == null) root._agentsScope = 'all'`), so the
  // declared default is null: 'all' as a default here would turn the seeds
  // into no-ops and hide the pages' own seeding contract.
  _canvasAgentsOnly: null,
  _canvasScope: null,
  _agentsScope: null,
  _agentsStatus: null,
  _agentsType: null,
  _agentsAssign: null,
  // Agent-editor capability/tool catalog — `sdk.getAgentToolCatalog()` takes
  // no scope argument (platform-wide config, the aiProviderDiagnostics
  // class). `null` = not fetched; the loader flag re-arms on failure.
  _agentToolCatalog: null,
  _agentToolCatalogLoaded: false,
  // TicketDetailModal dependency picker — query text + section collapse,
  // reset per ticket by the modal's own `el._fetchedForId` transition
  // (tickets/components/TicketDetailModal.js onUpdate).
  _depQuery: null,
  _depsOpen: false,
  // The open (?) help tooltip, by tip id — the brand QuestionMarkTooltip's
  // "one panel open at a time" latch (company/packages/brand/components/
  // QuestionMarkTooltip.js: `onMouseenter` writes it, the panel's `style`
  // factory + `styleDeps` and the button's `aria.expanded` + `ariaDeps` read
  // it off `s.root`).
  //
  // WHY THE SHELL HAS TO DECLARE A BRAND KEY. Measured live on
  // vault.pr.symbols.app (shell db2b1fd98, brand 3.14.787): every home MODULE
  // widget's (?) was dead while the static widgets' and the module pages'
  // (?) opened. Inside a module widget `s.root` is the MODULE store
  // (functions/moduleStateScope.js), whose signal traps split by key:
  //   • a READ of a key the module store does not own delegates to the shell
  //     root and subscribes the effect to the SHELL's signal;
  //   • a WRITE mints a LOCAL signal on the module store — unless the key is
  //     in SHELL_OWNED_STATE_KEYS, where `aliasShellOwnedKeys` has made the
  //     two stores share ONE signal object.
  // `helpTooltipOpenId` was declared nowhere in the shell, so it was not
  // shell-owned: the (?) button's hover wrote the module store's own signal
  // while its style/aria effects sat subscribed to the shell's. Measured on
  // the running page — after a real `mouseenter` the panel kept
  // `opacity: 0; visibility: hidden` and `aria-expanded="false"`, and forcing
  // the style effect to re-run painted the panel open immediately, i.e. the
  // factory was right and only the notification never arrived.
  // Declaring it here puts it in SHELL_OWNED_STATE_KEYS, so the module store
  // aliases the shell's signal for it and both halves meet on one signal.
  helpTooltipOpenId: null,
  // The ? keyboard sheet's open flag — the AppDialog stateKey of
  // components/ShortcutsOverlay.js, written by `?` (functions/shortcutsDefault.js
  // openShortcutsSheet) or the palette's row, cleared by the dialog's close
  // paths. Declared so the module store aliases the shell signal for it
  // (SHELL_OWNED_STATE_KEYS) — a palette row fired from a module surface writes
  // the SAME signal the shell-root sheet listens on.
  _shortcutsSheetOpen: null,
  // THE NEW-ACCOUNT WELCOME (functions/onboarding.js): the setup surface's
  // open flag (components/OnboardingWizard.js), the HOLD that covers a new
  // account's first load while its record is read (functions/onboarding.js
  // onboardingHoldNeeded), its flow ({ step, replay, progressed, picked,
  // picks, turns, ask, answered, seq, busy } — the wizard's picks and the
  // assistant's conversation live here as values and bundle KEYS, never
  // resolved text; the ready screen's flow adds `made`, the facts it froze
  // when it opened), the quick tour while it runs ({ at, ids, rect, total } —
  // functions/onboardingTour.js) and this session's decision (the copy of
  // `userPrefs.onboarding` that is newer than any read). Declared so the
  // root-write census classifies them (WORKSPACE_UNSCOPED_KEYS below).
  _onboardingOpen: null,
  _onboardingHold: false,
  _onboardingFlow: null,
  _onboardingTour: null,
  _onboardingDecision: null,
  // THE FLY ARRIVAL mark — true when this page load came through Fly's
  // hand-over (`?fly` in the address at boot, or the app builder's `&fly=`,
  // functions/onboarding.js onboardingNoteFlyArrival). It writes no decision:
  // it only keeps the new-account hold down and the full wizard closed for
  // this load, while Fly's own `pending` record (not this mark) is what owes
  // the person the one "Your workspace is ready" screen
  // (functions/onboardingReady.js). Per page load, never persisted.
  _onboardingFlyArrival: false,
  // Demo-session flag — written by bootShell's auth snapshot
  // (`resolveAuthSnapshot` → `{ isDemo: !!me.isDemo }`, spread into
  // `shell.state.update({ ...restAuth })` on the warm path). The write is
  // census-INVISIBLE (a spread carries no literal key), which is how it
  // stayed undeclared: nothing enforced it. Read by DemoClaimBanner.
  isDemo: false,
  // /admin/usage's bookkeeping (pages/admin/usage.js `_loadAll`): which
  // workspace the loaded numbers belong to, and the newest request's number.
  // The loader clears the numbers on a switch and drops an answer that is not
  // current; these two must SURVIVE the switch to do it (WORKSPACE_UNSCOPED).
  _usageWorkspaceId: null,
  _usageRequestId: 0,
  // /admin/usage's auto-top-up card (components/AdminAutoTopup.js,
  // functions/autoTopup.js, pages/admin/usage.js `_loadAll`). The rule, its
  // editor draft and step, the secure-setup URL, the consent box, the error
  // key, the touched flag and the in-flight flag belong to ONE workspace
  // (WORKSPACE_SCOPED: a switch wipes them, as the loader itself does on a
  // changed workspace). `_autoTopupRequest` is the answer counter
  // (functions/autoTopup.js `current()`): it must SURVIVE a switch
  // (WORKSPACE_UNSCOPED), like `_usageRequestId`.
  _autoTopup: null,
  _autoTopupDraft: null,
  _autoTopupStep: null,
  _autoTopupCheckoutUrl: null,
  _autoTopupConsent: false,
  _autoTopupError: null,
  _autoTopupTouched: false,
  _autoTopupBusy: false,
  _autoTopupRequest: 0,
  // /admin/usage: the return leg of a credit top-up checkout
  // (functions/topupReturn.js) — null, or { status: 'confirming' | 'paid' |
  // 'failed' | 'cancelled', sessionId, credits, error }. One workspace's
  // payment: WORKSPACE_SCOPED.
  _topupReturn: null,
  // /admin/usage: the person typed in (or cleared) the amount field — the
  // loader then leaves the field alone instead of filling in the server's
  // minimum (pages/admin/usage.js `_loadAll`). WORKSPACE_SCOPED, like the
  // loader's own reset of it on a changed workspace.
  _topupTouched: false,
  // The shell call panel (components/WorkspaceCallPanel.js). `_callPanel` is
  // the one call on screen (null = idle, hidden), written only by
  // functions/calls/workspaceCall.js — WORKSPACE_SCOPED: a call belongs to one
  // workspace's number; the switch stops the voice session first
  // (loadPrivateData.js, beside the LiveKit teardown). `_callPanelTick` is the
  // in-call timer's 1 s clock nudge — WORKSPACE_UNSCOPED, it carries no data.
  _callPanel: null,
  _callPanelTick: 0
}

export default STATE_DEFAULTS

// Combined workspace-switch wipe registry — unions state-app.js's
// WORKSPACE-PROJECT-tier WORKSPACE_SCOPED_KEYS (declared next to those
// defaults, in state-app.js — it can't import this file back, since this
// file imports IT) with the ORG/WORKSPACE/PROJECT-tier keys declared
// directly above. Declared here, next to those tiers' defaults, for the
// same reason state-app.js declares its own set next to ITS defaults: a new
// scoped key is flagged the moment it's added beside its default, instead
// of being tribal knowledge hand-listed in onWorkspaceChange
// (tickets/workspace.md "declarative WORKSPACE_SCOPED_KEYS registry").
//
// scopes.js's workspaceScopedWipe() loops this set + resets every key to
// this file's own default export (the fully-resolved EFFECTIVE default —
// e.g. `userDocs` is declared `[]` in state-app.js but shadowed to `null`
// right here by the ORG/WORKSPACE-tier layer above; reading through this
// file's default export gets the correct, already-resolved value), falling
// back to WORKSPACE_SCOPED_OVERRIDES for the few keys whose reset value
// isn't simply "this key's own default" (see state-app.js for why).
export const WORKSPACE_SCOPED_KEYS = new Set([
  ...APP_WORKSPACE_SCOPED_KEYS,
  // ORG scope
  'orgMembers',
  'orgRoles',
  'orgTeams',
  'permissions',
  // WORKSPACE scope
  // The current workspace's app-setup queue flag (boot fix step 4b): a switch
  // wipes it, and the new workspace's pass raises its own when it has apps.
  '_appsSettingUp',
  // /admin/usage's auto-top-up card: one workspace's rule, editor and
  // in-flight state (see the STATE_DEFAULTS entry; the counter is unscoped).
  '_autoTopup',
  '_autoTopupDraft',
  '_autoTopupStep',
  '_autoTopupCheckoutUrl',
  '_autoTopupConsent',
  '_autoTopupError',
  '_autoTopupTouched',
  '_autoTopupBusy',
  // /admin/usage: one workspace's top-up return line (see STATE_DEFAULTS).
  '_topupReturn',
  '_topupTouched',
  'workspaceMembers',
  'workspaceIntegrations',
  'workspaceBilling',
  // The alterable-AI-reference mute holds a RECORD ID from this tenant
  // ('record:<type>:<id>', pageContext.js) — meaningless and stale across a
  // switch, so it wipes. It also self-invalidates on any record change, so
  // the wipe is belt-and-braces, not load-bearing.
  '_aiRefMutedFor',
  // The composer's chosen AI context names this tenant's apps and routes
  // (functions/aiScope.js); inspect & ask's mode and outline are dropped with
  // the page they were over (functions/aiInspect.js).
  '_aiAskContext',
  '_aiInspect',
  '_aiInspectBox',
  '_creditGuard',
  '_creditBannerDismissed',
  '_creditBlockerDismissed',
  '_creditsShort',
  // Persona (PERSONA-5) — bound to one workspace; see the defaults' doc
  // comment above for why a switch must wipe it even though the SDK/server
  // already ended the session by the time this runs.
  '_personaSession',
  '_personaPickerOpen',
  '_personaExiting',
  // Public share-link dialog (PUBLIC-SHARE-LINKS-1) — see the defaults above
  // for why a workspace switch must wipe it.
  '_shareTarget',
  '_shareLinks',
  '_shareBusy',
  '_shareLoading',
  '_shareError',
  '_shareNewUrl',
  '_shareExpiryKey',
  'userDashboardPrefs',
  '_dashPrefsReadOk',
  'workspaceSettings',
  'intranetEvents',
  'intranetEventsLoaded',
  'intranetEventsError',
  // A workspace switch's NEW target starts not-ready until its full record
  // lands — see `workspaceReady`'s doc comment above.
  'workspaceReady',
  // PROJECT scope (canvas)
  'project',
  'projectComponents',
  'projectPages',
  'projectState',
  // WORKSPACE PROJECT scope
  'workspaceProject',
  // Per-session "server-rejected orgs" cache — reset so a new session's
  // claims aren't pre-filtered by a previous tier's 403'd ids.
  '_invalidOrgs',
  // WS-SWITCH-DRAIN-EPICSTREE-FILEPREVIEWS-1 — three keys measured riding a
  // real cross-org switch on :4506. None is in this file's stateDefaults, so
  // each has a reset shape in WORKSPACE_SCOPED_OVERRIDES (state-app.js):
  // `epicsTree` is declared only in tickets/state.js (epic-type tickets;
  // loadEpicsTree's non-empty guard KEEPS a foreign list, so the wipe is the
  // only thing that can clear it — 13 system epics showed in Bellforge's
  // picker); `_filePreviews` / `_fileThumbs` are files/functions/index.js's
  // per-file snippet + cover caches, keyed by file id — tenant file content.
  'epicsTree',
  '_filePreviews',
  '_fileThumbs',
  // WS-SWITCH-INCOMPLETE-1 leg 14 (2026-08-21) — THE PIPELINE DRAG POINTERS.
  // Declared in `packages/pipeline/state.js`, not in this file's
  // stateDefaults, so their reset shape lives in WORKSPACE_SCOPED_OVERRIDES
  // (state-app.js) exactly like `epicsTree` above. The stage-pipeline is an
  // ABSTRACT shared library — it never names a collection — but the two id
  // keys hold `String(s.id)` of a CONSUMING APP'S ROW (bellforge's /execute
  // card, crm's deal), so what sits in root after a drag is a tenant row id.
  // Registered scoped because a drag interrupted by a switch is exactly the
  // case `pipelineDragEnd` never runs for: dragstart writes `pipelineDragId`,
  // the user switches workspace mid-drag, and A's card id stays pointed at
  // while B's board renders — `pipelineDragOverCard` then compares B's ids
  // against it and `pipelineDrop` reads it as the card to move.
  'pipelineDragId',
  'pipelineDragOverStage',
  'pipelineDropBeforeId',
  // WS-UNDECLARED-KEYS-DRAIN-BATCH-1 — the first five keys drained from the
  // root-write census backlog. None is in this file's stateDefaults, so each
  // carries its reset shape in WORKSPACE_SCOPED_OVERRIDES (state-app.js),
  // argued there key by key. One line of scope reasoning each:
  //   `projectMembers`  — PROJECT scope, beside `project`/`projectComponents`
  //     above: it is `sdk.getProjectMembers(projectId)`'s row list, i.e. the
  //     people of ONE canvas project, so it names a foreign tenant's members
  //     the moment the project or the workspace changes.
  //   `_mcpConnectorDraft` — WORKSPACE scope: a half-typed MCP connector form
  //     (url/token/name) for the workspace being administered; a leftover
  //     draft would be saved INTO the next tenant's connector list.
  //   `activityScoring` — WORKSPACE PROJECT scope: fetched from
  //     `workspaceProject.activity.scoringConfig`, so every row is one
  //     workspace-project's scoring config, never a platform constant.
  //   `_usageTurnStats` — WORKSPACE scope: fetched as
  //     `ai.turnStats({ workspaceId: wsId })`; the id in the request IS the
  //     scope, so the answer belongs to that workspace alone.
  //   `roomSettingsDraft` — WORKSPACE scope: the meet room-settings modal's
  //     buffered draft for one room, and rooms are workspace records.
  'projectMembers',
  '_mcpConnectorDraft',
  'activityScoring',
  '_usageTurnStats',
  'roomSettingsDraft',
  // WS-ROOT-STATE-FORM2-WRITE-HOLE-1 — Form-2 census drain, batch 2. None is
  // in this file's stateDefaults, so each carries its reset shape in
  // WORKSPACE_SCOPED_OVERRIDES (state-app.js), argued there. One line each:
  //   `_agents` / `_agentsError` / `_agentsLoaded` — the ORG agent roster
  //     (`sdk.listAgents(workspace_org_id)`), its failure text, and its
  //     one-shot fetch gate. The exact `peopleLoaded` shape one tier down
  //     (FALSE-ZERO-HOME-NON-AGENT-KEYS-1): a stale `_agentsLoaded: true`
  //     suppresses the next tenant's refetch, so org A's agent names/emails
  //     render in org B forever. Three producers share the gate (home
  //     _loadHomeAgentRoster, canvas team-and-agents _loadAgents, simone
  //     Chat.ensureAgentRoster) — all mount/touch-gated, so the wipe re-arms
  //     them; home's org-change onUpdate re-runs its loader in place.
  //   `_agentAssignmentsLoaded` + `agentAssignmentsByAssignee` /
  //     `agentAssignmentsByAgent` — the agent↔person assignment maps and
  //     their gate. Both maps are keyed by GLOBAL userId ("the shape that
  //     leaks silently" — the `roles` lesson above): the same key is valid in
  //     every tenant, so a stale row never looks wrong. Rows carry orgId.
  //   `_integrationHub` — `loadIntegrationCatalogue(workspace_org_id)` rows
  //     (org's integration kinds + configured instances incl. has_secret
  //     flags) written to the SHELL root by every /admin/integrations* page;
  //     each page refetches in onCreate, so the wipe shows "loading", never
  //     a foreign org's connector list. (The marketplace island writes a key
  //     of the same name on its OWN island host — out of this registry's
  //     jurisdiction; see the census fixture.)
  '_agents',
  '_agentsError',
  '_agentsLoaded',
  '_agentAssignmentsLoaded',
  'agentAssignmentsByAssignee',
  'agentAssignmentsByAgent',
  '_integrationHub',
  // Admin fix lane D (2026-09-24) — /admin/logs' read failure:
  //   `_adminLogsError` — WORKSPACE scope: `true` when the workspace's audit
  //     log could not be read (`workspaceProject.auditLog` list, scoped by the
  //     workspace id), so the page says the read failed instead of "No activity
  //     events". It describes one workspace's read and must not follow the
  //     member into the next workspace; the page re-reads on mount.
  '_adminLogsError',
  // The shell call panel's one call (components/WorkspaceCallPanel.js) — a
  // call on workspace A's number never shows in workspace B. The live voice
  // session is stopped before the wipe (loadPrivateData.js).
  '_callPanel'
])

// ─────────────────────────────────────────────────────────────────────────
// WS-SWITCH-INCOMPLETE-1 (tickets/opus.md, Nika: "not everything switching …
// persists in different forms for a long time") — THE CLOSED-WORLD PARTITION.
//
// ⚠️ THE DEFECT THESE TWO SETS EXIST TO CLOSE was never that the registry was
// missing. `WORKSPACE_SCOPED_KEYS` above is real, declarative and single-
// sourced. **Its MEMBERSHIP was simply unenforced**: nothing related the set
// of STATE KEYS to the set of REGISTERED KEYS, and the only membership test
// was a hand-picked "representative keys" sample — which asserts the keys
// somebody remembered.
//
// The measured consequence: the registry held each domain's HEADLINE list and
// left its SATELLITES behind — feed 1 registered / 8 not, meet 3 / 16,
// ticket 3 / 7, standup 2 / 6, doc 5 / 7. **Whoever added a feature registered
// the list the switch visibly broke and left the rest.** That is exactly why
// the symptom returns in a NEW FORM rather than recurring identically: each
// past round registered one more satellite.
//
// So every key must now be classified into exactly one of THREE sets, and
// `__tests__/scopes.partition.test.js` fails if any key is in none or in two.
// **A newly added key belongs to no set ⇒ RED, naming itself.** That is the
// enforcement; everything else is another round of whack-a-mole.
// ─────────────────────────────────────────────────────────────────────────

// (2) REVIEWED AND DELIBERATELY NOT WIPED. Each group states WHY it survives a
// tenant boundary. Adding a key here is a claim that it carries no workspace
// data — make that claim consciously, and put it in the group that justifies it.
export const WORKSPACE_UNSCOPED_KEYS = new Set([
  // The call panel's in-call timer nudge (functions/calls/workspaceCall.js):
  // a wall-clock number, no tenant data.
  '_callPanelTick',
  // The /tickets board's mounted status (tickets/pages/board.js). A fact about
  // the DOM, not about the tenant: a workspace switch keeps the board mounted
  // (its `workspace` watcher reloads it), so a wipe would read false under a
  // board still on screen and stop its counts refresh until a remount. The
  // counts it gates ARE scoped (totalByColumn / totalByType / _rawTypeCounts).
  '_ticketsBoardLive',
  // WS-SHELL-MOUNT-TREE-BUILD-2700MS-1 — the ModalRoot `if:` latch. One-way
  // and app-lifetime: it says "the shared dialog host has been built", which
  // is a fact about the DOM, not about the tenant. Wiping it on a switch
  // would evaluate the latch false and DESTROY the live ModalRoot (an `if:`
  // flip removes the node), so every openModal after the first tenant switch
  // would warn and no-op.
  '_modalHostReady',
  // WS-SHELL-BOOT-TASK-LOAF-ACCEPTANCE-1 — the staged-mount ladder counter
  // (functions/shellStages.js). Exactly the same class as `_modalHostReady`
  // above, and for exactly the same reason: it is a monotonic fact about what
  // the DOM has ALREADY BUILT ("the shell has reached stage N"), not about the
  // tenant. The latches that read it are one-way, so a wipe on a switch could
  // not un-build anything — it would only make the counter disagree with the
  // tree, and any latch re-evaluated in that window would read 0 and destroy a
  // live subtree (an `if:` flip removes the node). The ladder runs once per
  // page load and is never re-armed on a switch.
  '_shellStage',
  // The ticket-modal fold latch (functions/ticketDetailModalLoad.js). Same
  // class as `_modalHostReady` / `_shellStage`: a one-way fact about what
  // the registry and the DOM already hold, never about the tenant.
  '_ticketModalReady',
  // The white-label host-scope pin MIRROR (customDomainBoot.js →
  // WhitelabelRequestAccess). Derived from the HOSTNAME, not the tenant:
  // app.<customer> pins the same org for the life of the page whoever is
  // signed in and whatever workspace is active, so a workspace switch must
  // NOT wipe it — wiping would blank the non-member gate's reactive input
  // while the module pin (hostScopePin.js, the auth authority) still holds.
  // Null on every platform host by the same fail-closed contract.
  '_hostScopeOrgId',
  // The host's tenant binding itself (seedWhitelabelHostScope) — the same
  // hostname-derived fact as the pin above, so the same reasoning: a
  // workspace switch must never wipe the boundary that limits which
  // workspaces this tab may switch between.
  'whitelabelHostScope',
  // WS-ROOTWRITE-CENSUS-CHAT-ROOM-KEYS-UNDECLARED-1 (2026-10-18) — the meet
  // rooms sidebar's archive list. BROWSER-GLOBAL BY CONSTRUCTION: the archive
  // stub (meet functions/archiveRooms.js) keeps ONE list in localStorage
  // (`meet_archivedRoomsIds`) for every workspace this browser opens, and the
  // /meet page re-reads that same list into this key on every mount (meet
  // pages/index.js). A switch wipe could not make it tenant-correct — the next
  // /meet mount restores it — and while /meet stays mounted it would un-hide
  // the rooms this workspace archived. Its entries are room ids, globally
  // unique, so an id from another workspace matches none of this workspace's
  // rooms. A workspace-keyed (or server-side) archive is the meet stub's own
  // change, not the switch's. The edit-mode pair and the archived view that
  // read it ARE scoped (state-app.js, the meet block).
  'archivedRoomsIds',
  // /admin/usage's bookkeeping (pages/admin/usage.js `_loadAll`): the
  // workspace the loaded numbers belong to and the newest request's number.
  // NOT tenant data — they are how the loader tells a switch happened and an
  // answer went stale; a wipe would make an old workspace's late answer look
  // current. The numbers themselves are cleared by the loader on a switch.
  '_usageWorkspaceId',
  '_usageRequestId',
  // The auto-top-up card's answer counter (functions/autoTopup.js `current()`,
  // bumped by the usage loader on a changed workspace): a wipe would let an
  // old workspace's in-flight answer match a new request number.
  '_autoTopupRequest',
  // Public share-link VIEWER state (/s/<token>, PUBLIC-SHARE-LINKS-1). The
  // recipient is anonymous by design — this surface has no tenant to be
  // scoped to, and a workspace switch cannot happen while it is on screen
  // (it renders outside the app chrome). Wiping it would be a no-op that
  // implies a tenancy the page does not have.
  '_sharedStatus',
  '_sharedDoc',
  '_sharedToken',
  '_sharedContentUrl',
  // INVITE FLOW — user-level onboarding beats, not tenant data. The accept
  // page raises `_inviteFlowActive` before any workspace scope exists, and
  // `_usernamePromptPending` + the `_unamePrompt*` modal carriers must
  // SURVIVE the joined-workspace switch (the nudge opens right after it);
  // a scoped wipe would eat the prompt mid-flow.
  '_inviteFlowActive',
  '_usernamePromptPending',
  '_unamePromptDraft',
  '_unamePromptBusy',
  '_unamePromptError',
  '_unamePromptStatus',
  '_unamePromptReason',
  // The campaigns card's group-by pill (WS-ANALYTICS-UTM-CAMPAIGNS-UI-1) —
  // a USER view preference ('campaign' | 'source' | 'medium' | 'content' |
  // 'term', declared in analytics/state.js), no tenant data in it; the feed
  // it selects (`_analyticsCampaigns`) IS scoped and refetches per tenant.
  '_analyticsCampaignsGroupBy',
  // Workspace presets (marketplace functions/workspacePresets.js, declared in
  // state-app.js's presets block): the PUBLIC preset registry
  // (GET /core/presets, one answer for every workspace and every visitor), the
  // time of its last good read, whether this host's SDK can read it at all,
  // and whether the last read failed. No tenant data in any of them — a wipe
  // would only make the next workspace read the same public list again. The
  // in-flight APPLY (`_presetApply`) names one workspace and IS scoped.
  '_workspacePresets',
  '_workspacePresetsAt',
  '_workspacePresetsUnsupported',
  '_workspacePresetsError',
  // USER identity + preferences — survive every tenant boundary; cleared only on logout
  'authBypass',
  'authUser',
  'cliAuth',
  'currentUserEmail',
  'currentUserId',
  'currentUserSupabaseId',
  'currentUserUsername',
  'jwtClaims',
  'lang',
  'perTabSessions',
  'preferences',
  'sdkSignedIn',
  'signedIn',
  // The sign-up → sign-in address handoff (shared pages/auth/signup.js
  // handToSignIn → signin.js, read once and cleared). PRE-AUTH: written and
  // read while signed out, when there is no workspace to switch; no tenant
  // data in it. A wipe could only lose the address mid-handoff.
  '_signInEmail',
  // Accessibility preferences — a person's contrast, motion, type-scale and
  // link-underline choices belong to THEM and to the device, not to whichever
  // workspace they happen to be in. Blanking these on a switch would take
  // someone's larger type or high contrast away mid-session, which is the
  // one class of setting where that is never acceptable. Same verdict, same
  // reason, as `themeMode` two lines down.
  'a11yContrast',
  'a11yFontScale',
  'a11yLinks',
  'a11yMotion',
  'themeMode',
  'themeResolved',
  // The home top's look (App strip style, names, summary style, sky;
  // functions/homeLook.js) — this session's decisions about a PERSON's
  // preference, the same class as `themeMode` beside it: it follows them to
  // every workspace, and a switch must not undo a choice they just made.
  '_homeLook',
  // The home tip's dismissal (functions/homeCtxTip.js) — the same class: a
  // PERSON's choice on this device, never workspace data.
  '_homeCtxTipDismissed',
  // The home tab's mode (functions/homeView.js, BLD-13): { workspaceId, mode,
  // barMode }. It carries its own workspace id and MUST survive a switch:
  // homeModeSync reads it in workspace B to give the person's own pick back
  // (a wipe first would leave A's home-tab mode live in B), then clears it.
  '_homeMode',
  // The window width's session decision (functions/slotSheetWidth.js) — the
  // same class.
  '_slotSheetWidth',
  // The /chat rail's dragged width (chat functions/chatSidebarResize.js) — the
  // same class: a layout choice with no workspace data in it.
  '_chatSidebarWidth',
  // The split view's divider (canvas PageSlot) — the same class: a layout
  // choice of the frame, with no workspace data in it.
  '_slotSplitRatio',
  'user',
  // TENANT identity itself — assigned by the switch, never blanked by it (see scopes.js: blanking `workspace` breaks chrome guards)
  '_pendingActiveOrgId',
  'cloudWorkspaces',
  'cloudWorkspacesError',
  'companyName',
  'org',
  'orgs',
  'workspace',
  'workspaceEmbed',
  'workspaces',
  // ENV / runtime facts — properties of the page or clock, not of a tenant
  '_bootFromCache',
  '_inShell',
  // The catch-all's "Not found" grace latch (see its STATE_DEFAULTS entry).
  // A fact about the page's CLOCK — the catch-all waited out its grace window
  // once this page load — with no tenant data in it. Its twin in the same
  // gate, `_appRoutesSettled`, is never wiped either, and it wins whenever it
  // is set. A wipe could only re-hide a "Not found" already on screen, and no
  // timer would reveal it again.
  '_notFoundGraceElapsed',
  'bridgeStatus',
  'bridgeUrl',
  'currentDay',
  'currentMinutes',
  'currentMonth',
  'currentOrigin',
  'currentPath',
  'currentYear',
  // DEPLOYMENT IDENTITY — a property of the HOST, not of the tenant.
  // `whitelabelBrand` is derived from `window.location.hostname`
  // (functions/whitelabelBrand.js) and stored on the ORG, not the workspace —
  // CustomDomainService returns {name,tagline,logoSvg,loaderLogoSvg,accent,
  // background,theme,personaSignins} and there is no workspace-level branding
  // field at all. A client-side switch cannot change the hostname, so the
  // correct value cannot change during a page's lifetime.
  // ⛔ And a wipe is PERMANENT for the session: `applyWhitelabelBrandLate` is
  // called only from the boot entry, no switch path calls it. Since the org
  // switcher raises the full-screen MainLoader and the white-label override
  // gates every branded prop on `s.root.whitelabelBrand`, wiping it flips a
  // customer's own domain to the Symbols spiral mid-switch and leaves it
  // generic until reload.
  // ⚠️ THE COUNTER-CASE, so it does not have to be re-derived: a white-label
  // host may legitimately display another tenant's data, which makes
  // branding-as-tenant-identity a fair concern. It does not change the answer
  // here — the lookup is host-keyed, so wiping yields NO brand rather than the
  // other tenant's. If that concern is real the fix is a RENDER GATE plus a
  // server change (projectBrandingForPublic projects away every identifier, so
  // the bag carries no organizationId to gate on) — not a registry entry.
  // Flip this one line if you disagree; the argument is here, not elsewhere.
  'whitelabelBrand',
  // The Fly rail put away or shown (functions/assistantFly.js
  // assistantFlyRailToggle, its only writer): a person's UI choice, kept on
  // the device, holding no tenant data — the same in every workspace, so a
  // switch keeps it (the rail of the next workspace's session opens the way
  // the person left it).
  '_aiFlyRailHidden',
  // The phone-viewport answer (functions/phoneNavigation.js phoneNavigationSync,
  // shared/components/MobileRailSheet.js): a fact about the device width, read
  // from the brand's mobileL query. A switch keeps the same screen; wiping it
  // would un-park the composer and drop the rails' phone gate until the next
  // resize. The phone dock's own panel and composer keys ARE scoped.
  '_isPhoneViewport',
  // DEVICE capture state — belongs to the mic/tab, not the workspace
  'voiceChatKey',
  'voiceEngine',
  'voiceEngineReason',
  'voiceError',
  'voiceFinal',
  'voiceHost',
  'voiceHostReady',
  'voiceInterim',
  'voiceLang',
  'voiceMicMuted',
  'voiceMinimized',
  'voiceMode',
  'voiceOpen',
  'voiceReply',
  'voiceResult',
  'voiceStartedAt',
  'voiceStatus',
  'voiceTurns',
  // UI INPUTS — the documented carry-across set (searchQuery/filter*/…) the user expects to persist
  // A transient focus flag of the open ticket modal — no workspace data in it,
  // and the modal closes on a workspace switch.
  '_assigneeFocused',
  '_assigneeQuery',
  '_broadcastQuery',
  // The launcher's category filter (components/SearchAppsMenu.js) — a view
  // filter, no workspace data in it; a group the next workspace lacks reads
  // as 'all' (SamSections), so carrying it across shows nothing foreign.
  // Its filter text and its search model likewise: typed app-name words,
  // reset on every trigger open, and which opener opened the panel.
  '_launcherFilter',
  '_launcherGroup',
  '_launcherMode',
  '_notifFilter',
  // The active option of an open ticket people list — a list position, no
  // workspace data in it; the modal closes on a workspace switch.
  '_peopleFieldActive',
  '_remindersFilter',
  // WS-SWITCH-INCOMPLETE-1 drain step 3 (bucket A2) — ⛔ THE ONE KEY IN
  // THE REMINDERS COMPOSER WHOSE BUCKET NAME POINTS THE WRONG WAY. It is
  // called a draft field, but its value is a MODE from a vocabulary the
  // source defines ('private' | 'shared'), byte-identical in every
  // workspace — and it is held in LOCKSTEP with `remindersScope`, two
  // lines above, which was reviewed unscoped for that same reason.
  // `setRemindersScope`'s early-return literally asserts the two are
  // equal. Wiping one half and not the other would desync the composer's
  // visibility toggle from the list it feeds — a state no writer in
  // functions/reminders.js can produce. A lockstep pair is classified as
  // a UNIT or not at all. See __tests__/remindersDraftTenantScope.test.js.
  '_reminderDraftShared',
  '_reporterQuery',
  'chatInput',
  'currentSearch',
  'docsQuery',
  'feedFilter',
  'filterAssignee',
  'filterLabel',
  'filters',
  'kbQuery',
  'navSearchQuery',
  'notesFilter',
  'peopleQuery',
  'searchQuery',
  'targetDraftCustom',
  'targetDraftCwd',
  'targetDraftMode',
  // UI VIEW/LAYOUT prefs — how a surface is displayed, not what it displays
  '_addAppScope',
  '_addAppPhonePane',
  '_broadcastScope',
  '_broadcastVisibleCount',
  '_chatCtxFolded',
  '_commentTab',
  '_documentsCtxFolded',
  '_mailCtxFolded',
  '_meetCtxFolded',
  '_profileTab',
  'activeSection',
  'activeTab',
  'assistantWidth',
  'collapsedGroups',
  // The reader's own note reading settings (documents functions/
  // notesReader.js) — a size, a line height and a column width from a
  // vocabulary the source defines, the same in every workspace. The person
  // keeps them across a switch, like the assistant's width beside it.
  'notesReader',
  'contentSection',
  'editorMode',
  // The History app's activity-type filter — a MODE from a vocabulary the
  // source defines ('ticket_update', 'standup', …), byte-identical in every
  // workspace; the person filter beside it names a roster email and is scoped.
  'historyType',
  'isRootState',
  'meetView',
  'modelMode',
  'profileModalTab',
  'remindersScope',
  'showEventDetail',
  'showFilterBar',
  'showLiveCaptions',
  'showMeetChat',
  'showMeetTranscript',
  'showNewBooking',
  'showNewChannel',
  'showRoomSettings',
  'showSessionMenu',
  'showSettings',
  'targetMode',
  'targetPickerView',
  'ticketEditMode',
  'viewMode',
  // UI TRANSIENT — overlays, anchors, drag state; closed or re-derived by the navigation the switch performs
  '_addComponentPicker',
  '_addPagePicker',
  '_aiModePickerAnchor',
  '_aiModePickerOpen',
  '_aiModePickerTick',
  '_aiPinMenuOpen',
  '_ax_showSessionMenu',
  // ⛔ `_canvasSlot` + `_canvasSlotSecond` LEFT THIS BLOCK on 2026-10-23
  // (WS-SWITCH-RESET-MISSES-SLOTS-AND-DRAFT-1). They sat here under this
  // block's reason — "closed or re-derived by the navigation the switch
  // performs" — and a workspace switch performs no such navigation: the URL
  // sync rewrites only the `/w/<org>/<ws-handle>` prefix and keeps every
  // route segment. Their value is a full tenant ROUTE ('/tickets/<id>',
  // '/documents/<id>'), not an overlay flag, so workspace A's pane page
  // re-mounted under workspace B. They are now registered scoped beside the
  // other pane keys (state-app.js, the PageSlot block).
  '_cvSelectionTick',
  '_db_showSessionMenu',
  '_dropdownLayer',
  '_homeThreadOpen',
  '_homeWidgetForm',
  '_infraIntent',
  '_mainThreadPath',
  '_mentionModal',
  '_modelMenuOpen',
  '_navSearchOpen',
  '_navbarDragOverAfter',
  '_navbarDragOverPath',
  '_navbarDragPath',
  '_profileEditing',
  '_promptFocused',
  '_promptMenuOpen',
  '_aiPanel',
  // the open view's room under a new answer's section: re-derived by the view
  // itself — a switch changes its conversation or Fly session, which clears it
  '_revealRoom',
  '_routeNudge',
  '_searchResultsInLauncher',
  '_simoneAgentPicker',
  '_slotNudge',
  '_teamInviteLinkCopied',
  '_versionsOpen',
  '_wsInviteLinkCopied',
  'activeDrag',
  'assistantOpen',
  'assistantResizing',
  'composerOpen',
  'composerPreview',
  'composerSubmitting',
  'contextDropdown',
  'createMenuOpen',
  'dragPreview',
  'dropPreview',
  // The open (?) help tooltip id (see the STATE_DEFAULTS entry). Pure UI
  // chrome about the pointer's position on THIS page — it carries no tenant
  // data, and a workspace switch re-renders the board anyway, so wiping it
  // would only close a panel that is already gone.
  'helpTooltipOpenId',
  // The ? keyboard sheet (see the STATE_DEFAULTS entry). A UI mode on the shell
  // root — no tenant data; a workspace switch leaves an open sheet open.
  '_shortcutsSheetOpen',
  // The new-account welcome (see the STATE_DEFAULTS entry). A PERSON's
  // onboarding beat, the class of `_usernamePromptPending`: the surface's
  // flag and its first-load hold, its flow (the wizard's picks and the
  // conversation — values and bundle keys, no tenant data; the workspace's
  // name is read live from `workspace`, which the switch reassigns), the
  // tour's stop box, and the person's own decision, which follows them to
  // every workspace like `_homeLook`. A wipe on a switch would end a setup or
  // a tour mid-way and let the welcome re-open for a decision already made.
  '_onboardingOpen',
  '_onboardingHold',
  '_onboardingFlow',
  '_onboardingTour',
  '_onboardingDecision',
  // …and how this page load arrived (the Fly arrival mark, see its
  // STATE_DEFAULTS entry): a fact about the load, not about a tenant.
  '_onboardingFlyArrival',
  'hidePickTool',
  'inspectorTools',
  'lightbox',
  'mentionPicker',
  'myViewQueueOpen',
  'notificationsPanelOpen',
  'picking',
  'quickCreate',
  'renameModal',
  'searchOpen',
  'selectedCategory',
  'selectedFolder',
  'shareLinkCopied',
  'sidebarDropdown',
  'targetPickerOpen',
  // TRANSPORT flags — in-flight/loading/typing booleans, re-derived by the switch itself
  //
  // ⛔ THE NAME IS A CLAIM, AND IT MUST STAY TRUE OF EVERY ENTRY. Corrected
  // 2026-08-19 (WS-SWITCH-INCOMPLETE-1): `_wsInviteStatus`/`_teamInviteStatus`
  // were filed here and were NOT booleans — objects `{tone, message, inviteUrl}`
  // carrying the previous workspace's invite URL, which the admin page rendered
  // and offered to copy. Nothing re-derived them on a switch either, so BOTH
  // halves of this heading were false for them; they now live in
  // WORKSPACE_SCOPED_KEYS (state-app.js, beside their defaults).
  // ⇒ Before adding a `*Status` / `*State` / `*Result` key here, check the VALUE
  // and not just the name: a key that holds an object belongs here only if that
  // object carries no tenant data, and "re-derived by the switch" must name the
  // code that actually re-derives it.
  '_aiActionRunning',
  '_aiSvcReady',
  '_aiTicketLoading',
  // ⛔ `_approvalPending` LEFT THIS SET on 2026-08-20 (WS-SWITCH-INCOMPLETE-1,
  // bucket A2) and it is the SECOND correction to this heading, in a new way.
  // It really was a boolean, so the first half of the heading held. The second
  // half — "re-derived by the switch itself" — held only by ACCIDENT: nothing
  // re-derives it, but its completion handler always ran and always wrote
  // `false`. Adding the `sameWorkspaceWriter` tenant guard to that handler (as
  // the same commit had to) makes the completion DROPPABLE, and a dropped
  // completion never clears the latch. It is scoped now, beside its four
  // siblings in state-app.js.
  // ⇒ Before adding an in-flight flag here, ask who clears it, and whether
  // that clearer can be SKIPPED. "The reply always writes false" is not
  // re-derivation by the switch; it is a dependency on the reply arriving.
  '_authReady',
  '_ax_chatTyping',
  '_broadcastSaving',
  '_commentsLoading',
  '_db_chatTyping',
  '_docSaving',
  '_followInFlight',
  // A boolean, no data. Re-derived by the switch: the new workspace's full
  // record re-runs applyShellLangPolicy, which pays it; a 7 s cap (on the
  // shell element) pays it whatever happens (functions/shellLangPolicy.js).
  '_langOwed',
  // The runtime loader's ready-hold re-render signal (a counter, no data):
  // carried across a switch on purpose, the hold itself lives on the element.
  '_loaderReadyHold',
  '_loginLoading',
  '_meetAnalyzeInFlight',
  '_navSearchLoading',
  '_profileLoading',
  '_profileSaving',
  '_wpSwitching',
  '_wsRefreshing',
  '_wsSwitching',
  'analyzing',
  'authReady',
  'chatTyping',
  'cloudWorkspacesLoading',
  'feedLoading',
  'loading',
  'meetCallInFlight',
  'meetChatUnread',
  'syncing',
  'transcriptPending',
  // WS-SWITCH-INCOMPLETE-1 drain step 6 — BUCKET B, the half of the overlay
  // pointers the rule sends HERE. The rule is written once, beside the twelve
  // that went the other way, in state-app.js's WORKSPACE_SCOPED_KEYS: a pointer
  // is scoped when its value is drawn from workspace-scoped data, unscoped when
  // its value comes from a vocabulary the SOURCE defines. Every value below is
  // a literal in the source, so none of them can name anything of workspace A.
  // ⛔ AND WIPING THEM WOULD BE ITS OWN BUG — a dock position or a reorder mode
  // is the USER'S choice, not the tenant's; a wipe would yank the assistant
  // back to the right edge mid-switch. See __tests__/bucketBOverlayPointers.test.js.
  '_assistantPillHandle',
  // Session marker of a Page-mode promotion (values: 'main' | null — a
  // vocabulary the source defines); the user's dock choice, like
  // `assistantDock` below, survives a switch.
  '_assistantDockPromotedFrom',
  '_navbarReorder',
  '_ticketFullPage',
  'assistantDock',
  // The assistant's MODE ('assistant' | 'dashboard' | 'apps' — a vocabulary
  // the source defines, functions/assistantMode.js): the person's choice, the
  // same class as `assistantDock` beside it; a switch must not undo it.
  'assistantMode',
  // Agent mode (functions/agentMode.js): the person's choice and a link
  // visit survive a switch; the runtime re-creates itself for the new
  // workspace (functions/agentModeRuntime.js), so its render counter and its
  // start error are not workspace data either.
  'agentMode',
  '_agentModeLink',
  '_agentModeRev',
  '_agentModeError',
  // What this page's shell + SDK render (`{ records: 1 | 0 }`,
  // assistantClientCapabilities): a fact about the SDK the page loaded,
  // the same in every workspace, so a switch keeps it (a wipe would only
  // read the same answer again).
  '_aiClientCaps',
  'hoverTime',
  // ⚠️ THE THIRD NAME `_selField` CAN RETURN, and the one no instrument saw.
  // `selectedTeamCategory` is the binary member-status lens ('active' |
  // 'inactive'), so the rule files it here — but it reached step 6 UNDECLARED,
  // invisible to the partition (no default ⇒ not in the universe) and to the
  // root-write census (computed-key write ⇒ not a literal). Its default is now
  // declared beside selectedGroup/selectedRole in state-app.js. See that
  // comment; it is the clearest example this ticket found of the enumeration
  // hole the whole ticket exists to close.
  'selectedTeamCategory',
  // ── WS-SWITCH-INCOMPLETE-1 step 8 (bucket E, 2026-08-21) — thirteen keys
  // that left WORKSPACE_SCOPE_UNREVIEWED for HERE, each on its WRITER and the
  // SERVER CALL it mirrors (see __tests__/bucketETenantScope.test.js).
  // USER profile page plane — three of the five `_profile*` keys are NOT
  // tenant rows (the other two, `_profileViewed` + `_profileActivity`, are
  // scoped in state-app.js):
  //   `_profileSlug` is the URL segment of /user/:slug — a string off
  //   window.location, nulled by the page's own onRemove (shared/pages/
  //   userProfile.js). `_profileProjects` is `listPublicProjects({ owner })`
  //   — the VIEWED user's public projects, keyed by owner slug, the same list
  //   in every workspace. `_profileSaveError` is the error text of
  //   `updateUserHrProfile(userId, …)` — a USER-record write with no
  //   workspace argument (its header rejects the caller-scoped route on
  //   purpose).
  '_profileProjects',
  '_profileSaveError',
  '_profileSlug',
  // USER follow graph — `followingEmails` has NO server call: it mirrors the
  // localStorage blob `marketplace.follows` (app.js boot hydrate, UserProfile
  // toggleFollow), and every reader falls back to that blob. A wipe would
  // only desync state from the disk copy that re-hydrates it on the next read.
  'followingEmails',
  // TOAST queue — `notifications` is the GlobalNotification plane: transient
  // text raised locally by `openNotification`, auto-dismissed by the
  // module-level `_timers` map. No rows, no server. (The notification CENTER
  // is `notifs`, below — a different key on purpose, state-app.js says so.)
  'notifications',
  // NOTIFICATION CENTER — `notifs` is the USER's inbox, not a workspace list:
  // `workspaceProject.notifications list` takes no argument and returns the
  // recipient's rows across workspaces (shared/functions/notificationScope.js:
  // "The inbox is NOT single-workspace… a row can name another org entirely",
  // which is why it ships a Workspace TAB). SETTLED AGAINST THE SERVER on this
  // ticket's live runs (legs 9 + 10: the same rows came back in both
  // workspaces — qualified, not residue). Its fetch memo is tenant-keyed
  // already (WS-SWITCH-MEMO-PLANE-1). ⛔ AND WIPE-HOSTILE: the bell's element
  // memo `el._loadedFor = email` gates the reload on EMAIL ONLY, so a wipe
  // without a forced refetch leaves the bell empty until remount.
  'notifs',
  // …and whether it answered once: it follows `notifs` (the user's inbox,
  // not a workspace list), so a switch keeps it with the rows it describes.
  'notifsLoaded',
  // PRESENCE — `onlineUsers` is fed by ONE global subscription,
  // `sdk.subscribePresence({ scope: 'symbols', … })` (shared/functions/
  // presence.js): org/company-wide, no workspaceId anywhere, so a switch
  // does not change who is online. ⛔ WIPE-HOSTILE: its writer keeps a
  // closure memo `lastOnlineSig`; the roster signature is unchanged across a
  // switch, so the next sync would skip the repair write and the strip would
  // stay empty until someone's status happened to change.
  'onlineUsers',
  // LOCAL CLAUDE-CODE BRIDGE — five keys written ONLY by `ws://localhost:8765`
  // handshake frames (functions/_simone/wsBridge.js handleBridgePayload) or
  // by `installSimoneAiTransport`: `agent` (the CLI label in the connection
  // badge, rewritten to 'sdk-ai' on every install), `target` (a local cwd /
  // cloud pairing), `targetOptions` (the user's machine's project list),
  // `targetSelected` (the pairing flag, default TRUE — `_freshEmpty` would
  // restore true, never false) and `recentPairings` (keyed by browser ORIGIN,
  // mirrored to localStorage `symbols_recent_pairings`). The user's machine
  // does not change when the workspace does.
  'agent',
  'target',
  'targetOptions',
  'targetSelected',
  'recentPairings',
  // SERVER-WIDE AI config — `aiProviderDiagnostics` is `GET /core/ai/providers`
  // (no workspace argument); shared/functions/aiProviderDiagnostics.js says it
  // itself: "intentionally NOT chat-scoped — server-wide config, identical for
  // every chat surface/scope in the session".
  'aiProviderDiagnostics',
  // ── WS-ROOT-STATE-FORM2-WRITE-HOLE-1 — Form-2 census drain, batch 2. ──
  // CANVAS AGENTS-PAGE LENSES — bucket-B rule (see `_assistantPillHandle`
  // above): every value comes from a vocabulary the SOURCE defines, so none
  // can name anything of workspace A. `_canvasAgentsOnly` (true/null) and
  // `_canvasScope` ('mine'/null) are route lenses the pages set on mount and
  // CLEAR in their own onRemove (canvas/pages/agents.js, my-agents.js) — a
  // wipe would fight the pages' own lifecycle. `_agentsScope` / `_agentsStatus`
  // / `_agentsType` / `_agentsAssign` are filter pills seeded 'all'; filters
  // are the documented carry-across family (UI INPUTS above).
  '_canvasAgentsOnly',
  '_canvasScope',
  '_agentsScope',
  '_agentsStatus',
  '_agentsType',
  '_agentsAssign',
  // PLATFORM-WIDE tool catalog — `sdk.getAgentToolCatalog()` carries no org/
  // workspace argument, so the rows are identical in every tenant: the
  // aiProviderDiagnostics class, gate included. (Contrast `_agentsLoaded`,
  // which gates an ORG-scoped call and is therefore scoped.)
  '_agentToolCatalog',
  '_agentToolCatalogLoaded',
  // TICKET-MODAL DEP PICKER — `_depQuery` is user-typed search text (the
  // carry-across UI INPUTS family) and `_depsOpen` a section collapse (UI
  // VIEW prefs family); BOTH are reset per ticket by the modal's own
  // `el._fetchedForId` transition, which runs before any cross-tenant read
  // could happen (a switch closes the modal's ticket either way).
  '_depQuery',
  '_depsOpen',
  // USER-SESSION fact — `isDemo` is `getMe().isDemo` (an unclaimed sandbox
  // demo account); a property of the signed-in user, rewritten by every boot
  // auth resolve, cleared server-side on /demo/claim. Not tenant data.
  'isDemo',
  // …and the same claim's pending state (components/DemoClaimBanner.js, the
  // only writer): the new email the claim parked on the server's
  // `pendingEmail` and whether the server could mail its link — the signed-in
  // person's OWN account, never tenant data, and a demo session is locked to
  // its one workspace (DemoRouteGuard), so no switch reaches it.
  '_demoClaimPendingEmail',
  '_demoClaimPendingSent'
])

// (3) ⚠️ QUARANTINE — NOT YET REVIEWED. **Quarantine is NOT a synonym for
// "unscoped", and it is NOT a to-do list to be bulk-moved into the registry.**
//
// These are the WS-SWITCH-INCOMPLETE-1 candidates: unregistered keys that are
// data-bearing enough that they may carry workspace-A content across a switch.
// They are listed here so they are VISIBLE and CANNOT SILENTLY GROW — not
// because anything is claimed about them either way.
//
// ⛔ DO NOT bulk-move this set into WORKSPACE_SCOPED_KEYS to "finish the job".
// Wiping state that legitimately carries across is a WORSE bug than the leak,
// and some of these may read as UI.
// ⛔ AND A NAME THAT READS AS UI IS NOT EVIDENCE THAT IT IS. This sentence
// used to offer THREE examples, and reading their writers disqualified all
// three (WS-SWITCH-INCOMPLETE-1, 2026-08-20):
//   • `_navSearchGrouped` is `_groupResults(navSearchResults)` — the ACTIVE
//     workspace's search hits, re-shaped, written in the same
//     `root.update({…})` as the rows themselves.
//   • `pickedElements` is the AI assistant's attached context: a picked ticket
//     carries its id, epic, repository and ASSIGNEE EMAIL, and sendPrompt puts
//     the array on the wire as `context.pickedElements`.
//   • `_slotFullscreen` is genuinely a UI tri-state — and still scoped, because
//     it SHADOWS the per-workspace persisted preference until it is null.
// ⇒ Follow the WRITER, and follow it to a MOUNT. `navUnreadCounts` used to sit
// here for the opposite reason — its only writer was IntranetAppHeader, which
// nothing renders — and it LEFT on 2026-09-06 when that stopped being true:
// mail's SSE reducer now writes `navUnreadCounts.mail` from a shell-scope
// stream that runs on every route (MAIL-BADGE-NOTIFICATIONS-WIDGET-1). A
// quarantine reason that says "no live writer" expires the moment one lands.
// Each key leaves this set ONE AT A TIME, on evidence:
// the live before/after switch diff named in the ticket's acceptance (snapshot
// every root key, switch, diff, attribute by A-identifying CONTENT — not by
// value-equality, since a workspace-independent key is legitimately unchanged).
// Then it moves to the registry (with a PHASE-2 refetch pair if wiping it
// leaves a surface empty) or to WORKSPACE_UNSCOPED_KEYS above with a reason.
export const WORKSPACE_SCOPE_UNREVIEWED = new Set([
  // ── WS-SWITCH-INCOMPLETE-1 step 8 (bucket E, 2026-08-21): 80 -> 35. ──
  // Forty-five keys left this set in one argued diff — 32 to
  // WORKSPACE_SCOPED_KEYS (state-app.js, each beside its registration) and
  // 13 to WORKSPACE_UNSCOPED_KEYS (above, each with its reason). EVERYTHING
  // STILL HERE IS HERE FOR A NAMED REASON, and none of them is "unreviewed"
  // any more — they are two families with owners elsewhere:
  //
  // (D) DEAD ROOT DEFAULTS — 31 keys with NO LIVE ROOT WRITER at HEAD when
  //     bucket E landed; 23 now (the deletion bullet below).
  //     Classifying a key that nothing writes says nothing about the switch;
  //     they are bucket D, owned by WS-ROOT-DEFAULTS-NO-WRITER-1's coordinated
  //     build/delete call, not by this drain. Moving them to either registry
  //     would only make this set look shorter. Sub-kinds, each proven by a
  //     grep with a `_ticketComments` positive control (31 hits):
  //     • no writer anywhere — `_standupUserName` (one READ, TicketDetailModal's
  //       author fallback; the survivor control of the tenant-scope suite),
  //       `customerRequests`, `inboundRequests`, `outboundRequests`,
  //       `editRequests`, `improvementRequests`, `auditReports` (the six
  //       ROUTED request/resolution keys — they stay until Nika's
  //       build/delete/flag call lands), `noGeneralTarget`, `presence`
  //       (chat/functions/subscribeRealtime.js: the Supabase presence track
  //       was RETIRED; zero readers).
  //     • DELETED 2026-08-29 by WS-ROOT-DEFAULTS-NO-WRITER-1 (31 -> 23), the
  //       declaration in state-app.js and the entry here in ONE diff:
  //       `_standupHistory`, `_standupMessages`, `_todayUpdate`,
  //       `reactionsByMessage`, `epicCounts` (fetchEpicCounts RETURNS its map,
  //       never writes root), `epicOptions`, `assistantDraft` (its own comment
  //       said the new simone Chat reads `chatInput`) — no reader either; and
  //       `suggestions`, read only by the gallery-only `ResolutionsMain`
  //       (Suggestions) page, deleted with it on Nika's answer D. Pinned gone
  //       by __tests__/deadRootDefaults.test.js.
  //     • written only `null` — `selectedDocument` (pageContext.js says so:
  //       "No editor reads `selectedDocument`, so the old bare update was
  //       dead").
  //     • HOST-written only — `feedPosts`, `_openThreads`, `_commentsByPost`,
  //       `composerDraft`, `composerError`, `_myProjects`: every write is
  //       `host.update?.()` on MarketplaceFeedIsland's OWN `state:` block,
  //       resolved by `useHostState(el, '_marketplaceHost')`; the root copies
  //       are shadowed defaults.
  //       ⛔ THIS ENTRY USED TO ADD "and `_marketplaceHost` is set nowhere in
  //       this shell", WHICH ARGUED THE OPPOSITE OF ITS OWN CONCLUSION and is
  //       corrected here (WS-SWITCH-RESET-MISSES-SLOTS-AND-DRAFT-1,
  //       2026-10-23). `useHostState`'s `_findHost` FALLS BACK to the nearest
  //       stateful ancestor when the predicate matches nothing, so a shell
  //       with no `_marketplaceHost` anywhere would route every one of these
  //       writes to THIS ROOT. The flag IS set in this shell — on
  //       MarketplaceFeedIsland itself — and the shell's `/marketplace/feed`
  //       route mounts that island (pages/index.js lazySharedLib entry ->
  //       marketplace/pages/feed.js, whose only child is the island), so the
  //       predicate matches before the walk reaches the root. The flag is
  //       what makes these shadowed, and it is pinned by
  //       marketplace/functions/communityDraftHostIsIsland.test.js — which
  //       also carries the no-island negative control showing the identical
  //       handler writing workspace A's project id onto the shell root.
  //       ⇒ AND NOTHING IS OWED AT THE ISLAND EITHER, so do not "finish the
  //       job" by teaching the island to wipe on a switch. The community
  //       feed carries NO tenant dimension: the posts and the follow graph
  //       are localStorage (marketplace/functions/community.js), and the
  //       composer's project picker is `sdk.listPublicProjects()` — the
  //       USER's own public projects, never a workspace-filtered list. A
  //       draft there is user data, so it legitimately outlives a tenant
  //       boundary, exactly like the UI-only filter inputs the registry
  //       deliberately leaves outside itself.
  //     • computed key with NO REACHABLE PREFIX — the six `_db_*` keys.
  //       `k = '_db_'` needs an ancestor with `chatScope: 'dashboard'`; the
  //       only chatScope mounts at HEAD are 'general' (Chat.js) and 'addapp'
  //       (pages/add-app.js), and homeAiSend.js records the retirement
  //       ("the inline dashboard thread is retired", 2026-07-19).
  //     • writer in an UNMOUNTED component — the sub-kind is now EMPTY.
  //       `navUnreadCounts` was its one member (writer: IntranetAppHeader,
  //       which nothing renders — app.js mounts the shared AppHeader — so
  //       AppNavbar's read always saw `{}`). It moved to
  //       APP_WORKSPACE_SCOPED_KEYS (state-app.js, the ⑮ MAIL block) on
  //       2026-09-06: the mail stream reducer gave it a LIVE MOUNTED writer,
  //       and the count it writes is a per-tenant fact. The chat half of the
  //       map still has no live writer; the key is scoped as a whole because
  //       the WIPE is per key, and its default `{}` is the correct reset for
  //       both halves.
  //
  // (L) LIVEKIT SESSION — DRAINED 2026-08-21, MEET-LIVE-CALL-SURVIVES-SWITCH-1.
  //     The 4 keys (`livekitConnected`, `livekitParticipants`,
  //     `livekitSpeakers`, `meetChatMessages`) that MIRROR the module-level
  //     `_livekitRoom` singleton in meet/functions/connectCall.js left this set
  //     for APP_WORKSPACE_SCOPED_KEYS (state-app.js, the meet block) in ONE
  //     LOCKSTEP diff with the teardown that makes the wipe truthful:
  //     `onWorkspaceChange` el.calls `disconnectCall` BEFORE its PHASE-1 wipe.
  //     Wiping the mirror without that teardown hides CallWidget over a room
  //     that is still publishing — the wrong state, not a fix — so if the
  //     teardown is ever removed these four return here in the same diff.
  //
  // ⭐ SURVIVOR CONTROL FOR THE LIVE PROBES FROM THIS LEG ON: `_standupUserName`
  // (bucket D, no writer, carries no tenant data — legitimately untouched by a
  // registry-driven wipe). `ticketDraft`, the previous control, drained in this
  // step.
  // (D) dead root defaults
  '_commentsByPost',
  '_db_activeProcesses',
  '_db_activeSessionId',
  '_db_chatMessages',
  '_db_currentAssistantId',
  '_db_runHistory',
  '_db_sessions',
  '_myProjects',
  '_openThreads',
  '_standupUserName',
  'auditReports',
  'composerDraft',
  'composerError',
  'customerRequests',
  'editRequests',
  'feedPosts',
  'improvementRequests',
  'inboundRequests',
  'noGeneralTarget',
  'outboundRequests',
  'presence',
  'selectedDocument'
])

export { WORKSPACE_SCOPED_OVERRIDES }
